import { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import NotificationPanel from './NotificationPanel';
import type { NotificationItem } from '@/api/notifications';

interface Props {
  unreadCount: number;
  notifications: NotificationItem[];
  soundEnabled: boolean;
  onSoundChange: (enabled: boolean) => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  align?: 'left' | 'right';
}

export default function NotificationBell({
  unreadCount,
  notifications,
  soundEnabled,
  onSoundChange,
  onMarkAsRead,
  onMarkAllAsRead,
  align = 'right',
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
        aria-label={`${unreadCount} notifications`}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-1">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <NotificationPanel
          notifications={notifications}
          unreadCount={unreadCount}
          soundEnabled={soundEnabled}
          onSoundChange={onSoundChange}
          onMarkAsRead={onMarkAsRead}
          onMarkAllAsRead={onMarkAllAsRead}
          onClose={() => setOpen(false)}
          align={align}
        />
      )}
    </div>
  );
}
